import React from 'react';

import { CreateOrUpdateProjectDialog } from '../Components/Views/Dialogs/CreateOrUpdateProjectDialog/CreateOrUpdateProjectDialog';
import { CreateProjectDialog } from '../Components/Views/Dialogs/CreateProjectDialog/CreateProjectDialog';
import { StmacLoginDialog } from '../Pages/Auth/StmacLoginDialog';
import { SingleChoiceDialog } from '../Pages/Projects/Components/ChoiceDialog/SingleChoiceDialog';
import { YesNoChoiceDialog } from '../Pages/Projects/Components/ChoiceDialog/YesNoChoiceDialog';
import { EditInputDialog } from '../Pages/Projects/Components/EditInputDialog';

//#region //* TYPES

export type DialogAction = () => void | Promise<void>;

export enum DialogStyle {
    Default,
    Danger,
}

export interface ConfirmDialogParameters {
    leftAction?: React.ReactNode;
    rightAction?: React.ReactNode;
    title: string;
    subtitle?: string;
    body?: string;
    onClickYes: (params: ValidateActionParams) => void;
    onClickNo?: (params: ValidateActionParams) => void;
    onClosed?: DialogAction;
    styles?: { rightAction?: React.CSSProperties };
    noHighlight?: boolean;
    yesHighlight?: boolean;
    enableCloseOutside?: boolean;
}

export type CreateOrSaveProjectParams = {
    projectName: string;
    setSaving: (saving: boolean) => void;
    closeDialog: (afterClose?: () => void) => void;
};

export type CreateProjectParams = {
    onValidate: (params: CreateOrSaveProjectParams) => void;
    onClosed?: DialogAction;
    cancelDisabled?: boolean;
};

export type CreateOrUpdateProjectParams = {
    currentProjectName: string;
    updateCurrentProject: (params: CreateOrSaveProjectParams) => void;
    createNewProject: (params: CreateOrSaveProjectParams) => void;
    onClosed?: DialogAction;
    cancelDisabled?: boolean;
};

export type EditInputDialogParams = {
    title: React.ReactNode;
    description?: React.ReactNode;
    subtitle?: React.ReactNode;
    placeholder?: string;
    initialValue?: string;
    onValidate: (params: InputValidateActionParams) => void;
};

export type ValidateActionParams = {
    setValidating: (busy: boolean) => void;
    closeDialog: (afterClose?: () => void) => void;
};

export type InputValidateActionParams = {
    input: string;
    setValidating: (busy: boolean) => void;
    closeDialog: (afterClose?: () => void) => void;
};

export type ShowStmacLoginDialogParams = {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    afterLoginSuccess: () => void;
    onClosed?: () => void;
    closeDisabled?: boolean;
};

export type ShowDeadEndErrorDialogParams = {
    title: string;
    body: string;
    labelChoice: string;
    onClickChoice: (params: ValidateActionParams) => void;
    onClosed?: DialogAction;
};

//#endregion

type DialogsContextType = {
    showEditInput: (props: EditInputDialogParams) => void;
    showStmacLogin: (props: ShowStmacLoginDialogParams) => void;
    showYesNoChoice: (props: ConfirmDialogParameters) => void;
    showDeadEndError: (props: ShowDeadEndErrorDialogParams) => void;
    showCreateProject: (props: CreateProjectParams) => void;
    showCreateOrUpdateProject: (props: CreateOrUpdateProjectParams) => void;
};

const DialogsContext = React.createContext<DialogsContextType>({
    showEditInput: () => {},
    showStmacLogin: () => {},
    showYesNoChoice: () => {},
    showDeadEndError: () => {},
    showCreateProject: () => {},
    showCreateOrUpdateProject: () => {},
});

export const useDialogs = () => React.useContext(DialogsContext);

export const DialogsProvider = ({ children }: React.PropsWithChildren<unknown>) => {
    const [confirm, setConfirm] = React.useState<ConfirmDialogParameters>();
    const [editInput, setEditInput] = React.useState<EditInputDialogParams>();
    const [stmacLogin, setStmacLogin] = React.useState<ShowStmacLoginDialogParams>();
    const [deadEnd, setDeadEndError] = React.useState<ShowDeadEndErrorDialogParams>();
    const [createProject, setCreateProject] = React.useState<CreateProjectParams>();
    const [createOrUpdateProject, setCreateOrUpdateProject] = React.useState<CreateOrUpdateProjectParams>();

    const showEditInput = (props: EditInputDialogParams): void => setEditInput(props);
    const showStmacLogin = (props: ShowStmacLoginDialogParams): void => setStmacLogin(props);
    const showDeadEndError = (props: ShowDeadEndErrorDialogParams): void => setDeadEndError(props);
    const showYesNoChoice = (props: ConfirmDialogParameters): void => setConfirm(props);
    const showCreateProject = (props: CreateProjectParams): void => setCreateProject(props);
    const showCreateOrUpdateProject = (props: CreateOrUpdateProjectParams): void => setCreateOrUpdateProject(props);

    const clear = (setState: (params?: any) => void, actionToDo?: () => void) => {
        setState(undefined);
        actionToDo?.();
    };

    return (
        <DialogsContext.Provider
            value={{
                showEditInput,
                showStmacLogin,
                showYesNoChoice,
                showDeadEndError,
                showCreateProject,
                showCreateOrUpdateProject,
            }}>
            {children}
            <>
                {confirm && <YesNoDialog {...confirm} closeDialog={(action) => clear(setConfirm, action)} />}
                {deadEnd && <DeadEndDialog {...deadEnd} closeDialog={(action) => clear(setDeadEndError, action)} />}
                {stmacLogin && (
                    <StmacLoginDialogWrapper {...stmacLogin} closeDialog={(action) => clear(setStmacLogin, action)} />
                )}
                {editInput && (
                    <EditInputDialogWrapper {...editInput} closeDialog={(action) => clear(setEditInput, action)} />
                )}
                {createProject && (
                    <CreateProjectDialogWrapper
                        {...createProject}
                        closeDialog={(action) => clear(setCreateProject, action)}
                    />
                )}
                {createOrUpdateProject && (
                    <CreateOrUpdateProjectWrapper
                        {...createOrUpdateProject}
                        closeDialog={(action) => clear(setCreateOrUpdateProject, action)}
                    />
                )}
            </>
        </DialogsContext.Provider>
    );
};

type DialogProps<T> = { closeDialog: (nextActionToDo?: () => void) => void } & T;
const YesNoDialog = ({ closeDialog, ...props }: DialogProps<ConfirmDialogParameters>) => {
    return (
        <YesNoChoiceDialog
            yesHighlight={false}
            noHighlight
            {...props}
            onClickYes={(setValidating) => props.onClickYes({ setValidating, closeDialog })}
            onClickNo={(setValidating) => props.onClickNo?.({ setValidating, closeDialog })}
            onClose={() => closeDialog(props.onClosed)}
        />
    );
};
const DeadEndDialog = ({ closeDialog, ...props }: DialogProps<ShowDeadEndErrorDialogParams>) => {
    return (
        <SingleChoiceDialog
            {...props}
            onClickChoice={(setValidating) => props.onClickChoice({ setValidating, closeDialog })}
            onClose={() => closeDialog(props.onClosed)}
        />
    );
};
const StmacLoginDialogWrapper = ({ closeDialog, ...props }: DialogProps<ShowStmacLoginDialogParams>) => {
    return (
        <StmacLoginDialog
            {...props}
            open
            onConnected={() => closeDialog(props.afterLoginSuccess)}
            onClose={() => closeDialog(props.onClosed)}
        />
    );
};
const EditInputDialogWrapper = ({ closeDialog, ...props }: DialogProps<EditInputDialogParams>) => {
    return (
        <EditInputDialog
            {...props}
            onValidate={(input, setValidating) => props.onValidate({ input, setValidating, closeDialog })}
            onClose={() => closeDialog()}
        />
    );
};
const CreateProjectDialogWrapper = ({ closeDialog, ...props }: DialogProps<CreateProjectParams>) => {
    return (
        <CreateProjectDialog
            {...props}
            validateAction={(projectName: string, setSaving: (saving: boolean) => void) => {
                props.onValidate({ projectName, setSaving, closeDialog });
            }}
            onClose={() => closeDialog(props.onClosed)}
        />
    );
};
const CreateOrUpdateProjectWrapper = ({ closeDialog, ...props }: DialogProps<CreateOrUpdateProjectParams>) => {
    return (
        <CreateOrUpdateProjectDialog
            {...props}
            updateCurrentProjectAction={(setSaving: (saving: boolean) => void) => {
                props.updateCurrentProject({
                    projectName: props.currentProjectName,
                    setSaving,
                    closeDialog,
                });
            }}
            createNewProjectAction={(projectName: string, setSaving: (saving: boolean) => void) => {
                props.createNewProject({ projectName, setSaving, closeDialog });
            }}
            onClose={() => closeDialog(props.onClosed)}
        />
    );
};
